import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, ElementRef, HostListener, input, viewChild } from '@angular/core';
import { Params, RouterLink } from '@angular/router';
import { AudioEffectPlayer } from '../../utils/audio.utils';

@Component({
  selector: 'rid-button',
  imports: [RouterLink, NgTemplateOutlet],
  templateUrl: './rid-button.html',
  host: {
    class: 'block',
  },
})
export class RidButton {
  content = input<string>();
  appearance = input<'SUCCESS' | 'NEUTRAL' | 'DANGER'>('SUCCESS');
  variant = input<'PRIMARY' | 'SECONDARY'>('PRIMARY');
  size = input<'SMALL' | 'NORMAL'>('NORMAL');
  disabled = input<boolean>(false);
  routerLink = input<string>();
  queryParams = input<Params>();
  keybind = input<string>();

  el = viewChild.required<ElementRef<HTMLElement>>('el');

  twVariant = computed<`${'PRIMARY' | 'SECONDARY'}_${'DISABLED' | 'NEUTRAL' | 'SUCCESS' | 'DANGER'}`>(
    () => `${this.variant()}_${this.disabled() ? 'DISABLED' : this.appearance()}`,
  );
  twClasses = computed(
    () =>
      `w-full font-default inline-block outline-none ${this.disabled() ? 'cursor-not-allowed' : 'cursor-pointer'} rounded-lg ${this.size() === 'SMALL' ? 'px-4 py-1' : 'px-8 py-2'} font-bold tracking-wider text-center select-none ${this.size() === 'SMALL' ? 'text-sm' : ''} transition-colors shadow-[0_0.25em_0] ${this.disabled() ? '' : 'active:shadow-[0_0_0] active:translate-y-1'} ${
        {
          PRIMARY_DISABLED: 'text-slate-400 bg-slate-200 border-2 border-transparent shadow-slate-300', //
          PRIMARY_DANGER:
            'text-white bg-red-500 border-2 border-transparent shadow-red-600 hover:bg-red-600 hover:shadow-red-700 focus:bg-red-600 focus:shadow-red-700',
          PRIMARY_SUCCESS:
            'text-white bg-emerald-500 border-2 border-transparent shadow-emerald-600 hover:bg-emerald-600 hover:shadow-emerald-700 focus:bg-emerald-600 focus:shadow-emerald-700',
          PRIMARY_NEUTRAL:
            'text-white bg-blue-500 border-2 border-transparent shadow-blue-600 hover:bg-blue-600 hover:shadow-blue-700 focus:bg-blue-600 focus:shadow-blue-700',
          SECONDARY_DISABLED: 'text-slate-400 bg-slate-200 border-2 border-slate-300 shadow-slate-300',
          SECONDARY_DANGER:
            'text-red-500 bg-white border-2 border-red-500 shadow-red-600 hover:border-red-600 hover:bg-slate-50 hover:shadow-red-700 focus:border-red-600 focus:bg-slate-50 focus:shadow-red-700',
          SECONDARY_SUCCESS:
            'text-emerald-500 bg-white border-2 border-emerald-500 shadow-emerald-600 hover:border-emerald-600 hover:bg-slate-50 hover:shadow-emerald-700 focus:border-emerald-600 focus:bg-slate-50 focus:shadow-emerald-700',
          SECONDARY_NEUTRAL:
            'text-blue-500 bg-white border-2 border-blue-500 shadow-blue-600 hover:border-blue-600 hover:bg-slate-50 hover:shadow-blue-700 focus:border-blue-600 focus:bg-slate-50 focus:shadow-blue-700',
        }[this.twVariant()]
      }`,
  );

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    const key = this.keybind();
    if (key && !this.disabled() && event.key.toLowerCase() === key.toLowerCase()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.click();
    }
  }

  click() {
    this.el().nativeElement.click();
  }

  onClick() {
    const effect = { PRIMARY: 'BUTTON_CLICK' as const, SECONDARY: 'HINT' as const }[this.variant()];
    AudioEffectPlayer.play(effect);
  }

  @HostListener('pointerenter')
  onHover() {
    if (!this.disabled()) {
      AudioEffectPlayer.play('BUTTON_HOVER', { decorative: true });
    }
  }
}
